import { useState } from 'react';
import { capitalize } from 'lodash';
import { parseISO } from 'date-fns';
import { NSBadge, NSInput } from 'bricks';
import { formatDateOnlyLong } from 'ns_libs/formatter';
import { DateField, TimelineStatusBootstrapColorMap } from 'views/DealDashboard/constants';
import { TimelineDraft, TimelineItem } from 'views/DealDashboard/Overview/Timeline/types';
import TimelineDatePicker from '../../TimelineDatePicker/TimelineDatePicker';
import AssigneeCell from '../../AssigneeCell/AssigneeCell';

const formatDateForAPI = (date: Date): string => {
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
    return adjustedDate.toISOString().split('T')[0];
};

type TimelineSlideoutDetailsProps = {
    timeline: TimelineItem;
    editMode: boolean;
    draft: TimelineDraft;
    orgId: string;
    onFieldChange: (patch: Partial<TimelineDraft>) => void;
    onAssign: (userCorrelationId: string) => void;
    onRemove: (userCorrelationId: string) => void;
};

const TimelineSlideoutDetails = ({
    timeline, editMode, draft, orgId, onFieldChange, onAssign, onRemove,
}: TimelineSlideoutDetailsProps) => {
    const [activeDateField, setActiveDateField] = useState<DateField | null>(null);

    const handleDateChange = (field: DateField) => (date: Date | null) => {
        setActiveDateField(null);
        onFieldChange({ [field]: date ? formatDateForAPI(date) : null });
    };

    const statusBadge = (
        <NSBadge color={TimelineStatusBootstrapColorMap[timeline.status] || 'primary-lighten'}>
            {capitalize(timeline.status)}
        </NSBadge>
    );

    if (!editMode) {
        const assigneeNames = (timeline.assignees ?? []).map(a => a.userName || 'Unknown');
        return (
            <div className="p-3 pr-4">
                <div className="d-flex align-items-center mt-2">
                    <span className="text-muted col-3 pl-0">Activity name:</span>
                    <span className="text-dark flex-grow-1" data-testid="timeline-slideout-name-display">{timeline.name}</span>
                </div>
                <div className="d-flex align-items-center mt-2">
                    <span className="text-muted col-3 pl-0">Target:</span>
                    <span className="text-dark flex-grow-1">{formatDateOnlyLong(timeline.target)}</span>
                </div>
                <div className="d-flex align-items-center mt-2">
                    <span className="text-muted col-3 pl-0">Date completed:</span>
                    <span className="text-dark flex-grow-1">{formatDateOnlyLong(timeline.completed)}</span>
                </div>
                <div className="d-flex align-items-center mt-2">
                    <span className="text-muted col-3 pl-0">Assignees:</span>
                    <span className="text-dark flex-grow-1">{assigneeNames.length ? assigneeNames.join(', ') : '-'}</span>
                </div>
                <div className="d-flex align-items-center mt-2">
                    <span className="text-muted col-3 pl-0">Status:</span>
                    <div className="flex-grow-1">{statusBadge}</div>
                </div>
                <div className="d-flex mt-2">
                    <span className="text-muted col-3 pl-0 pt-1">Description:</span>
                    <span className="text-dark flex-grow-1" data-testid="timeline-slideout-description-display">
                        {timeline.description || '-'}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 pr-4">
            <div className="d-flex align-items-center mt-2">
                <span className="text-muted col-3 pl-0">Activity name:</span>
                <div className="flex-grow-1">
                    <NSInput
                        name="timeline-slideout-name"
                        type="text"
                        value={draft.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange({ name: e.target.value })}
                        maxLength={255}
                        dataTestId="timeline-slideout-name-input"
                    />
                </div>
            </div>

            <div className="d-flex align-items-center mt-2">
                <span className="text-muted col-3 pl-0">Target:</span>
                <div className="flex-grow-1">
                    <TimelineDatePicker
                        value={draft.target ? parseISO(draft.target) : null}
                        onChange={handleDateChange(DateField.TARGET)}
                        field={DateField.TARGET}
                        isEditing={activeDateField === DateField.TARGET}
                        onEdit={() => setActiveDateField(DateField.TARGET)}
                        placeholder="Select target date"
                    />
                </div>
            </div>

            <div className="d-flex align-items-center mt-2">
                <span className="text-muted col-3 pl-0">Date completed:</span>
                <div className="flex-grow-1">
                    <TimelineDatePicker
                        value={draft.completed ? parseISO(draft.completed) : null}
                        onChange={handleDateChange(DateField.COMPLETED)}
                        field={DateField.COMPLETED}
                        isEditing={activeDateField === DateField.COMPLETED}
                        onEdit={() => setActiveDateField(DateField.COMPLETED)}
                        placeholder="Select completed date"
                    />
                </div>
            </div>

            <div className="d-flex align-items-center mt-2">
                <span className="text-muted col-3 pl-0">Assignees:</span>
                <div className="flex-grow-1">
                    <AssigneeCell
                        orgId={orgId}
                        assignees={draft.assignees}
                        onAssign={onAssign}
                        onRemove={onRemove}
                        placeholder="Select assignees"
                    />
                </div>
            </div>

            <div className="d-flex align-items-center mt-2">
                <span className="text-muted col-3 pl-0">Status:</span>
                <div className="flex-grow-1">{statusBadge}</div>
            </div>

            <div className="d-flex mt-2">
                <span className="text-muted col-3 pl-0 pt-1">Description:</span>
                <div className="flex-grow-1">
                    <NSInput
                        name="timeline-slideout-description"
                        type="textarea"
                        rows={3}
                        value={draft.description ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onFieldChange({ description: e.target.value })}
                        dataTestId="timeline-slideout-description"
                    />
                </div>
            </div>
        </div>
    );
};

export default TimelineSlideoutDetails;
